import type { ChannelKind } from './api';

/**
 * What each way of connecting a channel actually needs (context.md §45–§56). Phase 59.
 *
 * An omnichannel product is a product where "connect an account" is a form, and the form is
 * different for every provider. Describing them as data rather than as six branches in a component
 * means the form, the validation and the help text all come from one place — and adding a provider
 * is an entry here rather than a new screen.
 *
 * Every field says whether it is a **secret**. That is not decoration: a secret is sealed on the way
 * in, never sent back, and an empty box means "I did not retype it" rather than "delete it". The
 * form has to know which is which to be honest about what it is showing.
 */

export interface ProviderField {
  /** The key inside the channel's `config`, or one of the channel's own columns. */
  key: string;
  /** `config` unless it is a column on the channel itself. */
  on?: 'channel';
  labelKey: string;
  hintKey?: string;
  secret?: boolean;
  required?: boolean;
  placeholder?: string;
}

export interface ProviderSpec {
  provider: string;
  kind: ChannelKind;
  labelKey: string;
  descriptionKey: string;
  fields: ProviderField[];
  /**
   * Whether this provider receives through a webhook this product exposes.
   *
   * When it does, the screen has to show the URL and the verify token — they are the half of the
   * setup that happens in somebody else's dashboard, and a tenant who cannot see them cannot
   * finish.
   */
  webhook?: { path: string };
  /**
   * Described here, and not offered yet (Phase 130).
   *
   * A channel whose spec exists but whose product decision has not been made is worse than one that
   * is missing: connecting it costs the tenant an account with somebody else and gives them a
   * channel Safe Cortex is not ready to charge for. The entry stays so an already-connected channel
   * still renders its own form, and `connectableProviders()` leaves it out of the list.
   */
  comingSoon?: boolean;
}

export const PROVIDERS: ProviderSpec[] = [
  {
    provider: 'whatsapp_cloud',
    kind: 'WHATSAPP',
    labelKey: 'channels.provider.whatsapp_cloud',
    descriptionKey: 'channels.provider.whatsapp_cloud.hint',
    webhook: { path: '/v1/webhooks/whatsapp' },
    fields: [
      {
        key: 'address',
        on: 'channel',
        labelKey: 'channels.field.address',
        hintKey: 'channels.field.address.hint',
        required: true,
        placeholder: '+55 11 99999-8888',
      },
      {
        key: 'externalId',
        on: 'channel',
        labelKey: 'channels.field.phoneNumberId',
        hintKey: 'channels.field.phoneNumberId.hint',
        required: true,
        placeholder: '109876543210',
      },
      {
        key: 'businessAccountId',
        labelKey: 'channels.field.wabaId',
        hintKey: 'channels.field.wabaId.hint',
      },
      {
        key: 'accessToken',
        labelKey: 'channels.field.accessToken',
        hintKey: 'channels.field.accessToken.hint',
        secret: true,
        required: true,
      },
      {
        key: 'appSecret',
        labelKey: 'channels.field.appSecret',
        hintKey: 'channels.field.appSecret.hint',
        secret: true,
        required: true,
      },
      {
        key: 'verifyToken',
        labelKey: 'channels.field.verifyToken',
        hintKey: 'channels.field.verifyToken.hint',
        secret: true,
      },
    ],
  },
  {
    provider: 'whatsapp_web',
    kind: 'WHATSAPP',
    labelKey: 'channels.provider.whatsapp_web',
    descriptionKey: 'channels.provider.whatsapp_web.hint',
    fields: [
      {
        key: 'address',
        on: 'channel',
        labelKey: 'channels.field.address',
        hintKey: 'channels.field.addressWeb.hint',
        placeholder: '+55 11 99999-8888',
      },
    ],
  },
  {
    provider: 'twilio',
    kind: 'SMS',
    labelKey: 'channels.provider.twilio',
    descriptionKey: 'channels.provider.twilio.hint',
    comingSoon: true,
    webhook: { path: '/v1/webhooks/sms' },
    fields: [
      {
        key: 'address',
        on: 'channel',
        labelKey: 'channels.field.address',
        required: true,
        placeholder: '+55 11 99999-8888',
      },
      { key: 'accountSid', labelKey: 'channels.field.accountSid', required: true },
      { key: 'authToken', labelKey: 'channels.field.authToken', secret: true, required: true },
    ],
  },
  {
    provider: 'local',
    kind: 'WEBCHAT',
    labelKey: 'channels.provider.local',
    descriptionKey: 'channels.provider.local.hint',
    fields: [],
  },
];

export function providerSpec(provider: string): ProviderSpec | undefined {
  return PROVIDERS.find((spec) => spec.provider === provider);
}

/** What a tenant may connect today. Everything in `PROVIDERS` still describes itself. */
export function connectableProviders(): ProviderSpec[] {
  return PROVIDERS.filter((spec) => !spec.comingSoon);
}

/**
 * The URL a tenant pastes into the provider's dashboard.
 *
 * Built from where the browser is, because the API and the app are served from the same origin in
 * every deployment this product supports — and a URL somebody has to assemble by hand is a URL that
 * gets assembled wrong once and then debugged for a day.
 */
export function webhookUrl(spec: ProviderSpec, channelId: string): string | null {
  if (!spec.webhook) return null;

  return `${window.location.origin}${spec.webhook.path}/${channelId}`;
}
